/**
 * MHARHER Push Notifications Hook
 * Manages service worker registration, push subscription, and notification preferences
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Convert VAPID public key from base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const { api, isAuthenticated, user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [preferences, setPreferences] = useState(null);

  // Check browser support
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Register service worker
  useEffect(() => {
    if (!isSupported) return;

    async function registerSW() {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        console.log('[Push] Service Worker registered:', reg);
        setRegistration(reg);
        
        // Check existing subscription
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('[Push] Service Worker registration failed:', error);
      }
    }

    registerSW();
  }, [isSupported]);

  // Load preferences when authenticated
  useEffect(() => {
    if (!isAuthenticated || !api) return;
    
    async function loadPreferences() {
      try {
        const response = await api.get('/push/preferences');
        setPreferences(response.data.preferences);
      } catch (error) {
        console.error('[Push] Failed to load preferences:', error);
      }
    }
    
    loadPreferences();
  }, [isAuthenticated, api]);

  // Listen for notification clicks from service worker
  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handleMessage = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
        const { url, notification_id, type } = event.data.data || {};
        
        // Mark notification as read
        if (notification_id && api) {
          api.put(`/notifications/${notification_id}/read`).catch(console.error);
        }
        
        // Navigate to URL
        if (url && url !== window.location.pathname) {
          window.location.href = url;
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [api]);

  // Request permission and subscribe
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration || !isAuthenticated) {
      toast.error('Les notifications ne sont pas supportées');
      return false;
    }
    
    setLoading(true);
    
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Permission refusée. Active les notifications dans les paramètres du navigateur.');
        return false;
      }
      
      // Get VAPID public key from server
      const keyResponse = await api.get('/push/vapid-public-key');
      const vapidPublicKey = keyResponse.data.publicKey;
      
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }
      
      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      console.log('[Push] Subscription created:', subscription);
      
      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await api.post('/push/subscribe', {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      });
      
      setIsSubscribed(true);
      toast.success('Notifications activées!');
      
      return true;
      
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      toast.error('Erreur lors de l\'activation des notifications');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isSupported, registration, isAuthenticated, api]);

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    if (!registration) return false;
    
    setLoading(true);
    
    try {
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();
        
        // Remove from server
        await api.delete('/push/unsubscribe', {
          params: { endpoint: subscription.endpoint }
        });
      }
      
      setIsSubscribed(false);
      toast.success('Notifications désactivées');
      
      return true;
      
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      toast.error('Erreur lors de la désactivation');
      return false;
    } finally {
      setLoading(false);
    }
  }, [registration, api]);

  // Update preferences
  const updatePreferences = useCallback(async (newPreferences) => {
    if (!api) return false;
    
    try {
      const response = await api.put('/push/preferences', newPreferences);
      setPreferences(response.data.preferences);
      toast.success('Préférences mises à jour');
      return true;
    } catch (error) {
      console.error('[Push] Failed to update preferences:', error);
      toast.error('Erreur lors de la mise à jour');
      return false;
    }
  }, [api]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    if (!api || !isSubscribed) return false;
    
    try {
      await api.post('/push/test');
      toast.success('Notification de test envoyée!');
      return true;
    } catch (error) {
      console.error('[Push] Test notification failed:', error);
      toast.error('Erreur lors du test');
      return false;
    }
  }, [api, isSubscribed]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification
  };
}

export default usePushNotifications;
